// One-line calendar grammar (quick add on the calendar, MCP, voice): a line becomes an EVENT, a timed TASK
// or a REMINDER. Separate from parseQuickTitle (tasks), which keeps `!` = priority.
//   Call Fran @11am sat ~30m #sales        → event   kind call, Sat 11:00–11:30
//   Visit 12 Oak St tomorrow at 2:30pm      → event   kind visit
//   Cut trim @2pm                           → task    plannedStart 14:00
//   remind me to order trusses @10:45 !     → reminder at 10:45 today, style fullscreen (! = fullscreen, !! = alarm)
// Day words: today, tomorrow, mon…sunday (next such day, today if it is that day), 9/30, 2026-09-30.
import { normalizeTag } from './notes.js';
import { localDateStr } from './time.js';

export const EVENT_KINDS = ['call', 'visit', 'meeting', 'install', 'personal', 'other'];
const KIND_WORDS = { call: 'call', phone: 'call', ring: 'call', visit: 'visit', site: 'visit', meet: 'meeting', meeting: 'meeting', install: 'install', installation: 'install', deliver: 'install', delivery: 'install' };
const DEFAULT_MIN = { call: 30, visit: 60, meeting: 60, install: 240, personal: 60, other: 60 };
const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const DAY_WORD = /(?:^|\s)(today|tomorrow|tmrw|sun(?:day)?|mon(?:day)?|tue(?:s|sday)?|wed(?:nesday)?|thu(?:rs|rsday)?|fri(?:day)?|sat(?:urday)?)(?=\s|$)/i;
const DAY_NUM = /(?:^|\s)(?:on\s+)?(\d{4}-\d{2}-\d{2}|(\d{1,2})\/(\d{1,2}))(?=\s|$)/;
const TIME = /(?:^|\s)(?:@|at\s+)(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?(?=\s|$)/i;
// A bare time counts only when it can't be anything else: "6pm", "9 am", "6:30" (never a lone number or a 2x4).
const TIME_BARE = /(?:^|\s)(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)(?=\s|$)|(?:^|\s)(\d{1,2}):(\d{2})(?=\s|$)/i;
const EST = /(?:^|\s)~\s*(\d+(?:\.\d+)?)\s*(h|hr|hrs|hour|hours|m|min|mins)?(?=\s|$)/i;
const REL = /(?:^|\s)in\s+(\d+)\s*(m|min|mins|minutes|h|hr|hrs|hours)(?=\s|$)/i;

const cut = (s, m) => (s.slice(0, m.index) + ' ' + s.slice(m.index + m[0].length)).trim();
const addDays = (date, n) => { const d = new Date(`${date}T12:00:00`); d.setDate(d.getDate() + n); return localDateStr(d.getTime()); };

/** @returns {{ type: 'event'|'task'|'reminder', title, date, start?, end?, plannedStart?, at?, kind?, style?, tags, estimatedMinutes? }} */
export function parseQuickEvent(input, { today = localDateStr(Date.now()), now = Date.now() } = {}) {
  let s = String(input || '').trim();
  const out = { type: 'task', title: s, date: today, tags: [] };
  if (!s) return out;

  let reminder = false;
  const rem = s.match(/^(?:remind(?:er)?(?:\s+me)?\s*(?:to|:)?\s+)/i);
  if (rem) { reminder = true; s = s.slice(rem[0].length).trim(); }

  const bang = s.match(/\s*(!{1,2})$/);
  let style = null;
  if (bang && reminder) { style = bang[1].length === 2 ? 'alarm' : 'fullscreen'; s = s.slice(0, bang.index).trim(); }

  let date = today;
  const dw = s.match(DAY_WORD);
  if (dw) {
    const w = dw[1].toLowerCase();
    if (w === 'today') date = today;
    else if (w === 'tomorrow' || w === 'tmrw') date = addDays(today, 1);
    else { const target = DAYS.findIndex((d) => w.startsWith(d)); const cur = new Date(`${today}T12:00:00`).getDay(); date = addDays(today, (target - cur + 7) % 7); }
    s = cut(s, dw);
  }
  const dn = s.match(DAY_NUM);
  if (dn) {
    if (dn[2]) { const y = today.slice(0, 4); const cand = `${y}-${dn[2].padStart(2, '0')}-${dn[3].padStart(2, '0')}`; date = cand < today ? `${Number(y) + 1}${cand.slice(4)}` : cand; }
    else date = dn[1];
    s = cut(s, dn);
  }

  let minutes = null;
  let tm = s.match(TIME);
  if (!tm) { const b = s.match(TIME_BARE); if (b) { tm = b[4] ? Object.assign([b[0], b[4], b[5], undefined], { index: b.index }) : b; } }
  if (tm) {
    let h = parseInt(tm[1], 10); const mi = tm[2] ? parseInt(tm[2], 10) : 0;
    const ap = (tm[3] || '').toLowerCase().replace(/\./g, '');
    if (ap === 'pm' && h < 12) h += 12;
    if (ap === 'am' && h === 12) h = 0;
    if (!ap && h >= 1 && h <= 6) h += 12;          // "at 2" on a work calendar means 2 pm
    if (h >= 0 && h < 24 && mi >= 0 && mi < 60) minutes = h * 60 + mi;
    s = cut(s, tm);
  }
  const rl = s.match(REL);
  let at = null;
  if (rl) { const n = parseInt(rl[1], 10); at = now + n * (rl[2].startsWith('h') ? 3600000 : 60000) ; s = cut(s, rl); }

  let est = null;
  const es = s.match(EST);
  if (es) { const n = parseFloat(es[1]); const u = (es[2] || 'm').toLowerCase(); est = Math.round(u.startsWith('h') ? n * 60 : n); s = cut(s, es); }

  s = s.replace(/(^|\s)#([a-z][\w-]*)(?=\s|$)/gi, (m, pre, t) => { const n = normalizeTag(t); if (n && !out.tags.includes(n)) out.tags.push(n); return pre; }).trim();
  s = s.replace(/\s{2,}/g, ' ').trim();

  const first = s.split(/\s+/)[0]?.toLowerCase().replace(/[^a-z]/g, '');
  const kind = KIND_WORDS[first] || null;

  if (reminder) {
    const dayMs = new Date(`${date}T00:00:00`).getTime();
    return { type: 'reminder', title: s, date, at: at ?? (minutes != null ? dayMs + minutes * 60000 : null), style: style || 'banner', tags: out.tags };
  }
  if (kind) {
    const start = minutes;
    const len = est ?? DEFAULT_MIN[kind];
    return { type: 'event', kind, title: s, date, start, end: start != null ? start + len : null, estimatedMinutes: len, tags: out.tags, allDay: start == null };
  }
  return { type: 'task', title: s, date, plannedStart: minutes, estimatedMinutes: est, tags: out.tags };
}
